import React from "react";
import HoverIconButton from "components/HoverIconButton";
import ResourcesContainer from "containers/ResourcesContainer";
import { shortLinkCheckoutPreview } from "components/FormerEditor/common/preview";
import { secondaryBackButton, STATUS, UI_THEME } from "utils/constants/ui";
import { pluralizeCopy } from "utils/copy";
import Pill from "components/Pill";
import { STATE_KEYS } from "utils/constants/state";
import { useFormikContext } from "formik";
import classnames from "classnames";
import isEmpty from "lodash/isEmpty";
import { simulateFormSelections } from "utils/form";
import { useFormMergedShortLinkConfig } from "utils/hooks/manifest";
import { COLORS } from "utils/styles";
import { COMBINATION_CLEAR } from "utils/constants/confirm";
import get from "lodash/get";
import ConfirmIconButton from "components/ConfirmIconButton";
import { isCombinationResultEmpty } from "utils/combination";

const SET_COMBO_COPY = "Set combination";
const CombinationListItem = ({
  combination,
  onClick,
  active,
  fieldGroups,
  index,
  name
}) => {
  const indexLabel = index + 1;
  const shortLink = useFormMergedShortLinkConfig(combination.result);
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const fieldGroupsPath = `${name}.${STATE_KEYS.FORM.FIELD_GROUPS}`;
  const combinationPath = `${name}.${STATE_KEYS.FORM.CONFIG_LOGIC_CHECKOUTS_COMBOS}[${index}]`;

  const setResultPath = `${combinationPath}.result`;
  const hasResult = !isEmpty(get(values, setResultPath));
  const hasTouchedResult = get(touched, setResultPath);
  const clearResult = () => {
    setFieldValue(setResultPath, {});
    if (!hasTouchedResult) {
      setFieldTouched(setResultPath, true);
    }
  };

  return (
    <ResourcesContainer>
      {({ products, prices, coupons, taxRates, shippingRates }) => {
        let content = null;

        const hasCheckoutResult = !isCombinationResultEmpty(combination.result);

        const {
          checkout: { lineItems, banner, subscriptionScheduleItems }
        } = shortLinkCheckoutPreview({
          shortLink,
          products,
          prices,
          coupons,
          taxRates,
          shippingRates
        });

        const hasSubscriptionSchedule =
          Array.isArray(subscriptionScheduleItems) &&
          subscriptionScheduleItems.length > 0;

        const primaries = get(shortLink, STATE_KEYS.SHORT_LINK.PRIMARIES) || [];
        const remainingProducts = primaries.length - 1;
        const productLabel = hasCheckoutResult
          ? lineItems && lineItems[0] && lineItems[0].label
          : SET_COMBO_COPY;
        const remainingProductLabel =
          hasCheckoutResult && remainingProducts > 0
            ? `+ ${pluralizeCopy("product", remainingProducts)}`
            : null;

        const amountLabel = hasCheckoutResult ? get(banner, "label", "") : "";
        const sublabel = hasCheckoutResult ? get(banner, "sublabel", "") : "";
        const schedule =
          hasCheckoutResult &&
          hasSubscriptionSchedule &&
          `${subscriptionScheduleItems[0].label}: ${subscriptionScheduleItems[0].value}`;

        content = (
          <div className="flex flex-column w-100 bg-white--hover">
            <div className="flex flex-row w-100 justify-between items-center pa2 ba br--top br2 hairline-1">
              <Pill
                theme={UI_THEME.SLIM}
                copy={indexLabel}
                customClasses={{
                  container: "flex flex-row w-100 items-end justify-start"
                }}
              />
              <Pill
                theme={UI_THEME.SLIM}
                status={hasCheckoutResult ? STATUS.SUCCESS : STATUS.WARNING}
                copy={hasCheckoutResult ? "Complete" : "Incomplete"}
              />
            </div>
            <div className="flex flex-column pa2 bl br hairline-1">
              <div className="flex flex-row justify-between items-start">
                <div className="f5 fw5 lh-title pr2">{productLabel}</div>
                {amountLabel && <div className="fw6 f5">{amountLabel}</div>}
              </div>
              {remainingProductLabel || sublabel ? (
                <div
                  className={classnames("flex flex-column f7 pt2", {
                    "justify-between": remainingProductLabel && sublabel,
                    "justify-end": !remainingProductLabel && sublabel
                  })}
                >
                  {remainingProductLabel ? (
                    <div className="lh-copy pb1">{remainingProductLabel}</div>
                  ) : null}
                  <div className="f7">{sublabel}</div>
                </div>
              ) : null}
              {schedule && (
                <div className="flex flex-row f7 pt2 justify-end">
                  {schedule}
                </div>
              )}
            </div>
          </div>
        );

        return (
          <div className="flex flex-column w-100 bb hairline-1 relative relative">
            <div
              onClick={onClick}
              className="flex flex-column w-100 relative pointer relative bh--inset--hover pt3 ph3"
            >
              {content}
            </div>
            <div className="flex w-100 justify-end ph3 pb3">
              <div
                className={classnames(
                  "flex flex-row pa2 bt hairline-1 ba br2 br--bottom w-100",
                  {
                    "justify-between": hasCheckoutResult,
                    "justify-end": !hasCheckoutResult
                  }
                )}
              >
                {hasCheckoutResult && (
                  <div className="dib flex flex-row">
                    <HoverIconButton
                      theme={UI_THEME.SLIM}
                      icon="eye"
                      tooltip={!active ? "Preview" : ""}
                      color={active ? COLORS.blue : COLORS.primary}
                      onClick={simulateFormSelections({
                        values,
                        name: fieldGroupsPath,
                        setFieldValue,
                        selections: combination.selections
                      })}
                    />
                    <div className="pl2">
                      <ConfirmIconButton
                        key={`clear-${hasResult}`}
                        icon="refresh"
                        tooltip="Clear"
                        slides={COMBINATION_CLEAR}
                        onConfirm={clearResult}
                        disabled={!hasResult}
                      />
                    </div>
                  </div>
                )}
                <HoverIconButton
                  theme={UI_THEME.SLIM}
                  icon="right"
                  onClick={onClick}
                />
              </div>
            </div>
          </div>
        );
      }}
    </ResourcesContainer>
  );
};

export default CombinationListItem;
